import { useCallback, useEffect, useState } from 'react';
import { FlatList, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ApiError } from '../../api/client';
import { getHomeDashboard } from '../../api/home';
import { FigmaHomeSurface } from '../../components/business/FigmaHomeSurface';
import { EmptyView } from '../../components/ui/EmptyView';
import { ErrorView } from '../../components/ui/ErrorView';
import { LoadingView } from '../../components/ui/LoadingView';
import { ScreenBackground } from '../../components/ui/ScreenBackground';
import { showAppToast } from '../../components/ui/AppToast';
import { tokens } from '../../theme/tokens';
import { getPrototypeSession } from '../../utils/prototypeSession';
import type { HomeDashboardViewModel } from '../../types/home';

type HomePageState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'empty' }
  | { status: 'success'; dashboard: HomeDashboardViewModel };

const isDashboardEmpty = (dashboard: HomeDashboardViewModel): boolean =>
  dashboard.todayPlans.length === 0 && dashboard.knowledgeCards.length === 0;

const getWeakPointLabel = (dashboard: HomeDashboardViewModel): string => {
  const weakestKnowledgeCard = dashboard.knowledgeCards.reduce<
    HomeDashboardViewModel['knowledgeCards'][number] | null
  >((current, item) => {
    if (!current || item.masteryPercent < current.masteryPercent) {
      return item;
    }
    return current;
  }, null);

  return weakestKnowledgeCard?.title ?? '今日薄弱点';
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.code === 'unauthorized') {
      return '登录状态已失效，请重新登录';
    }
    return error.message;
  }
  return '暂时无法加载首页数据';
};

export default function HomePage() {
  const router = useRouter();
  const [state, setState] = useState<HomePageState>({ status: 'loading' });
  const [gradeLabel, setGradeLabel] = useState('初三');

  const loadDashboard = useCallback(async () => {
    setState({ status: 'loading' });
    try {
      const dashboard = await getHomeDashboard();
      if (isDashboardEmpty(dashboard)) {
        setState({ status: 'empty' });
        return;
      }
      setState({ status: 'success', dashboard });
    } catch (error: unknown) {
      setState({ status: 'error', message: getErrorMessage(error) });
    }
  }, []);

  const syncPrototypeSession = useCallback(async () => {
    const session = await getPrototypeSession();
    if (session.gradeLabel) {
      setGradeLabel(session.gradeLabel);
    }
  }, []);

  useEffect(() => {
    void loadDashboard();
    void syncPrototypeSession();
  }, [loadDashboard, syncPrototypeSession]);

  const openPhotoSearch = useCallback(() => {
    router.push('/photo-search');
  }, [router]);

  const openGradeSelect = useCallback(() => {
    router.push('/grade-select');
  }, [router]);

  const showDevelopmentToast = useCallback((featureName: string) => {
    showAppToast(`${featureName}功能开发中`, 'info');
  }, []);

  const openPracticeReview = useCallback(() => {
    router.push('/study-plan');
  }, [router]);

  const openAiAssistant = useCallback(() => {
    router.push('/ai-assistant');
  }, [router]);

  const openWrongBook = useCallback(() => {
    router.push('/wrong-questions');
  }, [router]);

  const openSpecialPractice = useCallback(() => {
    router.push('/study-plan');
  }, [router]);

  if (state.status === 'loading') {
    return (
      <SafeAreaView style={styles.screen}>
        <ScreenBackground variant="frame" style={styles.background}>
          <LoadingView message="正在加载首页" />
        </ScreenBackground>
      </SafeAreaView>
    );
  }

  if (state.status === 'error') {
    return (
      <SafeAreaView style={styles.screen}>
        <ScreenBackground variant="frame" style={styles.background}>
          <ErrorView message={state.message} onRetry={loadDashboard} />
        </ScreenBackground>
      </SafeAreaView>
    );
  }

  if (state.status === 'empty') {
    return (
      <SafeAreaView style={styles.screen}>
        <ScreenBackground variant="frame" style={styles.background}>
          <EmptyView
            title="暂无学习任务"
            description="今天还没有安排任务，可以先从拍照搜题开始一次学习闭环。"
            actionLabel="重新加载"
            onAction={loadDashboard}
          />
        </ScreenBackground>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.screen}>
      <ScreenBackground variant="frame" style={styles.background}>
        <FlatList
          data={[]}
          keyExtractor={(_, index) => `home-empty-${index}`}
          renderItem={null}
          contentContainerStyle={styles.content}
          ListHeaderComponent={
            <FigmaHomeSurface
              gradeLabel={gradeLabel}
              weakPointLabel={getWeakPointLabel(state.dashboard)}
              onPressGrade={openGradeSelect}
              onPressTopCamera={openPhotoSearch}
              onPressPhotoSearch={openPhotoSearch}
              onPressPracticeReview={openPracticeReview}
              onPressDedicatedExplain={openAiAssistant}
              onPressWrongBook={openWrongBook}
              onPressSpecialPractice={openSpecialPractice}
              onPressTranslation={() => showDevelopmentToast('拍照翻译')}
              onPressComposition={() => showDevelopmentToast('语文作文')}
            />
          }
          ListFooterComponent={<View style={styles.footerSpacer} />}
        />
      </ScreenBackground>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    alignItems: 'center',
    backgroundColor: tokens.colors.appFrame,
  },
  background: {
    flex: 1,
    width: '100%',
  },
  content: {
    width: '100%',
    paddingTop: 16,
  },
  footerSpacer: {
    height: tokens.spacing.xl,
  },
});
